import React, { useRef, useState } from 'react';
import type { ControlBridgeViewModel } from '../deriveViewModel';
import { SectionLabel } from '../shared/SurfaceParts';
import styles from '../ControlBridge.module.css';
import {
  entropyDisplayLabel,
  entropyDisplayScore,
  formatVgrGlanceValue,
  glanceEntropyPresentation,
  trendDirectionLabel,
  vgrTrendDirection,
  type McDomainKey,
  type McDomainVgrTile,
} from './mcDomainVgr';
import {
  criticalActionForViewer,
  nextMovePriorityLabel,
  nextMoveStatusLabel,
  type McNextMoveCard,
} from './mcNextMove';
import { entropyBarClass } from '../entropyLanguage';
import { domainGlanceFraming } from './missionControlHelpers';
import { EngineeringWorkspacesPanel } from './EngineeringWorkspacesPanel';
import { MissionControlWorkshopAssessment } from './MissionControlWorkshopAssessment';
import type { KryspaceControlBridgeStructure } from '../../../types/kryspaceControlBridgeStructure';
import {
  isCompanyArtifactInstanceId,
  resolveMcDomainArtifactInstanceId,
} from '../detail/resolveArtifactInstanceId';

export type MissionControlDensity = 'expanded' | 'compact';

export interface MissionControlPersonalLayerProps {
  vm: ControlBridgeViewModel;
  density?: MissionControlDensity;
  onRequestExpand?: () => void;
  structure?: KryspaceControlBridgeStructure;
  onOpenArtifactDetail?: (artifactInstanceId: string, invokingLabel: string) => void;
  /**
   * Domain nodes may be company artifacts (Fred UUID / ``ca-*``) or artifact items
   * (DDD ``ai-*-business-domain``). Item ids must not call the CA detail endpoint.
   */
  onOpenItemDetail?: (itemId: string, invokingLabel: string) => void;
  /** Deep-link next-move cards into Exo focus. */
  onNavigateToExo?: (itemId: string) => void;
  /**
   * When an MC domain tile expands, set sidebar / queue scope to the CA filterScope
   * (not the tile keyword). Pass ``null`` on collapse to restore the prior scope.
   * Customer/Stakeholder on Fred may have no real CA — expand then does not call
   * with a scope id (synthetic nodes are skipped by resolve).
   */
  onSelectDomainScope?: (filterScope: string | null) => void;
  /** When true, engineering workspace links live in header/sidebar model picker instead. */
  /** Logged-in resource profile. Critical action today is limited to this assignee. */
  viewerResourceRef?: string;
  hideEngineeringWorkspaces?: boolean;
  /** Navigate into an engineering CB workspace (parent remembers primary). */
  onOpenEngineeringWorkspace?: (slug: string) => void;
}

const DOMAIN_ICONS: Record<McDomainKey, string> = {
  business: 'B',
  stakeholder: 'S',
  customer: 'C',
};

function FramingLines({
  more,
  blocking,
  where,
  blockingItemId,
  onOpenBlocking,
}: {
  more: string;
  blocking: string;
  where: string;
  blockingItemId?: string;
  onOpenBlocking?: (itemId: string) => void;
}) {
  const canOpen = Boolean(onOpenBlocking && blockingItemId);
  return (
    <dl className={styles.mcFraming} data-testid="mc-framing-lines">
      <div className={styles.mcFramingRow}>
        <dt>More</dt>
        <dd>{more}</dd>
      </div>
      <div className={styles.mcFramingRow}>
        <dt>Blocking</dt>
        <dd>
          {canOpen ? (
            <button
              type="button"
              className={styles.mcFramingBlockingBtn}
              data-testid="mc-framing-blocking"
              onClick={(event) => {
                event.stopPropagation();
                onOpenBlocking?.(blockingItemId!);
              }}
            >
              {blocking}
            </button>
          ) : (
            blocking
          )}
        </dd>
      </div>
      <div className={styles.mcFramingRow}>
        <dt>Where</dt>
        <dd>{where}</dd>
      </div>
    </dl>
  );
}

function NextMoveCard({
  label,
  card,
  testId,
  onOpen,
}: {
  label: string;
  card: McNextMoveCard | null;
  testId: string;
  onOpen?: (itemId: string) => void;
}) {
  if (!card) {
    return (
      <article className={styles.mcNextMoveCard} data-testid={testId}>
        <span className={styles.mcNextMoveLabel}>{label}</span>
        <span className={styles.mcNextMoveEmpty}>Not ranked in manifest</span>
      </article>
    );
  }
  const priority = nextMovePriorityLabel(card.priority);
  const itemId = card.itemId;
  const canOpen = Boolean(onOpen && itemId);
  return (
    <article
      className={styles.mcNextMoveCard}
      data-testid={testId}
      role={canOpen ? 'button' : undefined}
      tabIndex={canOpen ? 0 : undefined}
      onClick={
        canOpen && itemId
          ? () => {
              onOpen?.(itemId);
            }
          : undefined
      }
      onKeyDown={
        canOpen && itemId
          ? (event) => {
              if (event.key !== 'Enter' && event.key !== ' ') return;
              event.preventDefault();
              onOpen?.(itemId);
            }
          : undefined
      }
    >
      <span className={styles.mcNextMoveLabel}>{label}</span>
      <div className={styles.mcNextMoveHeader}>
        {card.statusPhrase ? (
          <span className={styles.mcStatusPill}>{nextMoveStatusLabel(card.statusPhrase)}</span>
        ) : null}
        {priority ? <span className={styles.mcNextMovePriority}>{priority}</span> : null}
      </div>
      <span className={styles.mcNextMoveTitle}>{card.title}</span>
      {card.subtitle ? <span className={styles.mcNextMoveSubtitle}>{card.subtitle}</span> : null}
      {canOpen ? <span className={styles.mcNextMoveSubtitle}>Open in Exo</span> : null}
    </article>
  );
}

function DomainGlanceTile({
  tile,
  framing,
  isCritical,
  compact,
  expanded,
  onToggle,
  onCompactActivate,
  onViewArtifact,
  onOpenBlocking,
}: {
  tile: McDomainVgrTile;
  framing: {
    more: string;
    blocking: string;
    where: string;
    blockingItemId?: string;
  };
  isCritical: boolean;
  compact: boolean;
  expanded: boolean;
  onToggle: () => void;
  onCompactActivate?: () => void;
  onViewArtifact?: () => void;
  onOpenBlocking?: (itemId: string) => void;
}) {
  const trend = vgrTrendDirection(tile.trendSeries);
  const glance = formatVgrGlanceValue(tile.vgrValue);
  const compactEntropy = glanceEntropyPresentation(tile);
  const entropyScore = entropyDisplayScore(tile);
  const expandedEntropyTone = entropyBarClass(entropyScore);
  // C8: never alarm-paint underived VGR/entropy on MC tiles (compact or expanded).
  const underived = !glance.derived || tile.entropyClass === 'undefined';
  const entropyTone = underived ? compactEntropy.tone : compact ? compactEntropy.tone : expandedEntropyTone;
  const reservedLabel = tile.clientEntropyLabel?.trim() || null;
  const stateLabel = entropyDisplayLabel(tile);
  const entropyTitle = underived
    ? compactEntropy.title
    : stateLabel
      ? stateLabel
      : entropyScore != null
        ? `Entropy ${entropyScore}`
        : reservedLabel
          ? reservedLabel
          : 'Entropy not assessed';
  // Compact non-MC band: never mark critical from missing-VGR ranking noise.
  const showCritical = !compact && isCritical && glance.derived;

  return (
    <div
      className={[
        styles.mcDomainVgrTile,
        compact ? styles.mcDomainVgrTileCompact : '',
        showCritical ? styles.mcDomainVgrTileCritical : '',
        expanded ? styles.mcDomainVgrTileExpanded : '',
      ]
        .filter(Boolean)
        .join(' ')}
      data-testid={`mc-domain-tile-${tile.domain}`}
      role="button"
      tabIndex={0}
      aria-label={compact ? `${tile.title} — open Mission Control` : undefined}
      aria-expanded={compact ? undefined : expanded}
      onClick={compact ? onCompactActivate : onToggle}
      onKeyDown={(event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        if (compact) {
          onCompactActivate?.();
          return;
        }
        onToggle();
      }}
    >
      <div className={styles.mcDomainVgrTileHeader}>
        <span className={styles.mcDomainVgrIcon} aria-hidden>
          {DOMAIN_ICONS[tile.domain]}
        </span>
        <span className={styles.mcDomainVgrLabel}>{tile.title}</span>
        <span
          className={`${styles.mcDomainVgrEntropyDot} ${styles[`mcDomainVgrEntropyDot_${entropyTone}`]}`}
          title={entropyTitle}
          aria-label={entropyTitle}
          data-testid={`mc-domain-entropy-${tile.domain}`}
        />
      </div>
      <div className={styles.mcDomainVgrBody}>
        <span
          className={styles.mcDomainVgrValue}
          data-testid={`mc-domain-vgr-glance-${tile.domain}`}
          data-derived={glance.derived ? 'true' : 'false'}
        >
          {glance.text}
        </span>
      </div>
      {expanded ? (
        <div className={styles.mcDomainVgrExpandPanel} data-testid={`mc-domain-vgr-detail-${tile.domain}`}>
          <FramingLines
            {...framing}
            onOpenBlocking={onOpenBlocking}
          />
          {trend !== 'flat' ? (
            <span className={styles.mcDomainVgrTrend} data-trend={trend}>
              {trendDirectionLabel(trend)}
            </span>
          ) : null}
          {onViewArtifact ? (
            <button
              type="button"
              className={styles.mcDomainViewArtifactBtn}
              data-testid={`mc-view-artifact-${tile.domain}`}
              onClick={(event) => {
                event.stopPropagation();
                onViewArtifact();
              }}
            >
              View artifact
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export const MissionControlPersonalLayer: React.FC<MissionControlPersonalLayerProps> = ({
  vm,
  density = 'expanded',
  onRequestExpand,
  structure,
  onOpenArtifactDetail,
  onOpenItemDetail,
  onNavigateToExo,
  onSelectDomainScope,
  hideEngineeringWorkspaces = false,
  viewerResourceRef,
  onOpenEngineeringWorkspace,
}) => {
  const compact = density === 'compact';
  const [expandedDomain, setExpandedDomain] = useState<McDomainKey | null>(null);
  /** Domain whose expand successfully set CA filterScope — only that domain restores on collapse. */
  const scopeOwnerDomainRef = useRef<McDomainKey | null>(null);

  const applyDomainScope = (domain: McDomainKey) => {
    if (!structure || !onSelectDomainScope) return;
    const scopeId = resolveMcDomainArtifactInstanceId(structure, domain);
    if (!scopeId) {
      // Synthetic / unresolved domain (e.g. Fred Customer) — do not touch shell scope.
      return;
    }
    scopeOwnerDomainRef.current = domain;
    onSelectDomainScope(scopeId);
  };

  const toggleDomain = (domain: McDomainKey) => {
    const expanding = expandedDomain !== domain;
    setExpandedDomain(expanding ? domain : null);
    if (!structure || !onSelectDomainScope) return;

    if (expanding) {
      applyDomainScope(domain);
      return;
    }

    // Collapsing this tile: restore only if this domain owns the MC-driven scope.
    if (scopeOwnerDomainRef.current === domain) {
      scopeOwnerDomainRef.current = null;
      onSelectDomainScope(null);
    }
  };

  /** Compact band: Business/Stakeholder/Customer return to MC focus and set that domain scope. */
  const activateCompactDomain = (domain: McDomainKey) => {
    onRequestExpand?.();
    applyDomainScope(domain);
  };

  return (
    <div
      className={`${styles.mcHeaderStrip} ${compact ? styles.mcHeaderStripCompact : styles.mcHeaderStripExpanded}`}
      data-testid="mission-control-personal-layer"
      data-density={density}
    >
      {compact && onRequestExpand ? (
        <button
          type="button"
          className={styles.mcOpenStripBtn}
          data-testid="mc-open-mission-control"
          onClick={() => onRequestExpand()}
        >
          Mission Control
        </button>
      ) : null}
      {vm.mcDomainVgr ? (
        <section aria-label="At a glance" data-testid="mc-at-a-glance">
          {!compact ? <SectionLabel>At a glance</SectionLabel> : null}
          {vm.mcDomainVgr.mostCriticalDomain && !compact ? (
            <p className={styles.mcDomainCriticalCallout} data-testid="mc-most-critical-domain">
              Most critical:{' '}
              <strong>
                {vm.mcDomainVgr.mostCriticalDomain.charAt(0).toUpperCase() +
                  vm.mcDomainVgr.mostCriticalDomain.slice(1)}
              </strong>
            </p>
          ) : null}
          {vm.mcDomainVgr.mostCriticalDomain && !compact ? (
            <p className={styles.mcDomainCriticalCallout} data-testid="mc-constraint-sentence">
              {domainGlanceFraming(
                vm,
                vm.mcDomainVgr.mostCriticalDomain,
                vm.mcDomainVgr.tiles.find((tile) => tile.domain === vm.mcDomainVgr?.mostCriticalDomain)
                  ?.title ?? vm.mcDomainVgr.mostCriticalDomain
              ).blocking}
            </p>
          ) : null}
          <div className={`${styles.mcDomainVgrGrid} ${compact ? styles.mcDomainVgrGridCompact : ''}`}>
            {(compact ? vm.mcDomainVgr.tiles.slice(0, 3) : vm.mcDomainVgr.tiles).map((tile) => {
              const domainEntityId = structure
                ? resolveMcDomainArtifactInstanceId(structure, tile.domain)
                : undefined;
              const openAsCompanyArtifact =
                Boolean(domainEntityId) && isCompanyArtifactInstanceId(domainEntityId!);
              const canViewArtifact =
                Boolean(domainEntityId) &&
                (openAsCompanyArtifact
                  ? Boolean(onOpenArtifactDetail)
                  : Boolean(onOpenItemDetail));
              const framing = domainGlanceFraming(vm, tile.domain, tile.title);
              return (
              <DomainGlanceTile
                key={tile.domain}
                tile={tile}
                framing={framing}
                isCritical={vm.mcDomainVgr?.mostCriticalDomain === tile.domain}
                compact={compact}
                expanded={!compact && expandedDomain === tile.domain}
                onToggle={() => toggleDomain(tile.domain)}
                onCompactActivate={() => activateCompactDomain(tile.domain)}
                onOpenBlocking={onNavigateToExo}
                onViewArtifact={
                  canViewArtifact && domainEntityId
                    ? () => {
                        if (openAsCompanyArtifact) {
                          onOpenArtifactDetail?.(domainEntityId, 'Mission Control');
                        } else {
                          onOpenItemDetail?.(domainEntityId, 'Mission Control');
                        }
                      }
                    : undefined
                }
              />
              );
            })}
          </div>
        </section>
      ) : null}

      {vm.bridgeSessionAssessment && !compact ? (
        <MissionControlWorkshopAssessment model={vm.bridgeSessionAssessment} compact={compact} />
      ) : null}

      {vm.mcEngineeringWorkspaces && !hideEngineeringWorkspaces && !compact ? (
        <EngineeringWorkspacesPanel
          model={vm.mcEngineeringWorkspaces}
          compact={compact}
          onOpenWorkspace={onOpenEngineeringWorkspace}
        />
      ) : null}

      {vm.mcNextMove && !compact ? (
        <section aria-label="Next move" data-testid="mc-next-move">
          <SectionLabel>Next move</SectionLabel>
          {vm.mcNextMove.primarySessionLabel ? (
            <p className={styles.mcNextMoveSession}>{vm.mcNextMove.primarySessionLabel}</p>
          ) : null}
          <div className={styles.mcNextMoveGrid}>
            <NextMoveCard
              label="Next waypoint"
              card={vm.mcNextMove.nextWaypoint}
              testId="mc-next-waypoint"
              onOpen={onNavigateToExo}
            />
            <NextMoveCard
              label="Critical action today"
              card={criticalActionForViewer(
                vm.mcNextMove.criticalAction,
                vm.eligibleWork,
                viewerResourceRef
              )}
              testId="mc-critical-action"
              onOpen={onNavigateToExo}
            />
            <NextMoveCard
              label="Critical blocker"
              card={vm.mcNextMove.criticalBlocker}
              testId="mc-critical-blocker"
              onOpen={onNavigateToExo}
            />
          </div>
        </section>
      ) : null}
    </div>
  );
};
